import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import PageHead from "../components/PageHead";
import { toast } from "sonner";
import { ArrowsClockwise, Trophy, TrendUp, TrendDown, Clock } from "@phosphor-icons/react";

function winColor(w) {
  if (w == null) return "text-muted-foreground";
  if (w >= 60) return "text-bull";
  if (w >= 40) return "text-neutral";
  return "text-bear";
}

export default function Performance() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/performance").then((r) => setRows(r.data.performance)).catch(() => {});
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/performance/refresh"); toast.success("Outcomes re-evaluated against live prices"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const top = rows[0];
  return (
    <>
      <PageHead title="Group Performance" subtitle="Which desks actually deliver — by target-hit vs stop-loss rate"
        right={<button data-testid="perf-refresh" onClick={refresh} disabled={busy}
          className="brand-gradient text-white rounded-full px-4 py-2.5 text-sm font-semibold flex items-center gap-2 shadow-lg hover:opacity-90 disabled:opacity-60 transition-opacity duration-200">
          <ArrowsClockwise size={15} weight="bold" className={busy ? "animate-spin" : ""} /> Re-evaluate
        </button>} />

      {top && top.win_rate != null && (
        <div className="glass-strong rounded-3xl p-6 mb-4 flex items-center gap-4 fade-up">
          <div className="w-14 h-14 rounded-2xl brand-gradient flex items-center justify-center shadow-lg"><Trophy size={26} weight="fill" className="text-white" /></div>
          <div>
            <div className="text-xs text-muted-foreground">Top performing source</div>
            <div className="font-display font-extrabold text-xl">{top.group_name}</div>
            <div className="text-sm text-bull font-mono">{top.win_rate}% win rate · {top.target}/{top.target + top.sl} closed calls</div>
          </div>
        </div>
      )}

      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold px-6 py-3">Source Group</th>
                <th className="text-right font-semibold px-3 py-3">Total Calls</th>
                <th className="text-right font-semibold px-3 py-3">Targets Hit</th>
                <th className="text-right font-semibold px-3 py-3">Stopped Out</th>
                <th className="text-right font-semibold px-3 py-3">Open</th>
                <th className="text-right font-semibold px-6 py-3">Win Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.group_name} data-testid={`perf-row-${g.group_name}`} className="border-b border-border/50 hover:bg-foreground/5 transition-colors duration-150">
                  <td className="px-6 py-3 font-semibold">{g.group_name}</td>
                  <td className="px-3 py-3 text-right font-mono">{g.total}</td>
                  <td className="px-3 py-3 text-right font-mono text-bull"><TrendUp size={12} weight="bold" className="inline mb-0.5" /> {g.target}</td>
                  <td className="px-3 py-3 text-right font-mono text-bear"><TrendDown size={12} weight="bold" className="inline mb-0.5" /> {g.sl}</td>
                  <td className="px-3 py-3 text-right font-mono text-muted-foreground"><Clock size={12} weight="bold" className="inline mb-0.5" /> {g.open}</td>
                  <td className={`px-6 py-3 text-right font-mono font-bold ${winColor(g.win_rate)}`}>{g.win_rate == null ? "—" : `${g.win_rate}%`}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No calls tracked yet. Run the pipeline, then re-evaluate.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">Outcomes are evaluated by comparing each call's target / stop-loss to the live price. "Open" calls haven't hit either level yet.</p>
    </>
  );
}
